import {
  IQuoteTemplateActions,
  IQuoteTemplateTranslationFetchAction,
  IQuoteTemplateTranslationUpdateAction,
  IQuoteTemplateSetOrderPayload,
  ItemContext
} from './actions.types';
import {
  IQuoteFreightTemplateData,
  IQuotePaymentTermTemplateData,
  IQuoteServiceTemplateData,
  IQuoteTemplateData,
  IQuoteTermTemplateData
} from '@/models';
import { IQuoteTemplateMutationsEnum } from '../mutations';
import { IQuoteTemplateFilterParams } from '@/services/quotetemplate';

export const actions: IQuoteTemplateActions = {
  // Services
  fetchQuoteServiceTemplates(context: ItemContext): Promise<void> {
    return this.$services.quoteServiceTemplate
      .fetchQuoteTemplates()
      .then(templates => {
        context.commit(
          IQuoteTemplateMutationsEnum.SET_QUOTE_SERVICE_TEMPLATES,
          templates
        );
      });
  },

  fetchQuoteServiceTemplate(
    context: ItemContext,
    payload: number
  ): Promise<void> {
    return this.$services.quoteServiceTemplate
      .fetchQuoteTemplate(payload)
      .then(template => {
        const index = context.state.serviceTemplates.findIndex(
          v => v.id === template.id
        );
        if (index === -1) {
          context.commit(
            IQuoteTemplateMutationsEnum.ADD_QUOTE_SERVICE_TEMPLATE,
            template
          );
        } else {
          context.commit(
            IQuoteTemplateMutationsEnum.UPDATE_QUOTE_SERVICE_TEMPLATE,
            template
          );
        }
      });
  },

  createQuoteServiceTemplate(
    context: ItemContext,
    payload: IQuoteServiceTemplateData
  ): Promise<void> {
    return this.$services.quoteServiceTemplate
      .createQuoteTemplate(payload)
      .then(template => {
        context.commit(
          IQuoteTemplateMutationsEnum.ADD_QUOTE_SERVICE_TEMPLATE,
          template
        );
      });
  },

  updateQuoteServiceTemplate(
    context: ItemContext,
    payload: IQuoteServiceTemplateData
  ): Promise<void> {
    return this.$services.quoteServiceTemplate
      .updateQuoteTemplate(payload)
      .then(template => {
        const index = context.state.serviceTemplates.findIndex(
          v => v.id === template.id
        );
        if (index === -1) {
          context.commit(
            IQuoteTemplateMutationsEnum.ADD_QUOTE_SERVICE_TEMPLATE,
            template
          );
        } else {
          context.commit(
            IQuoteTemplateMutationsEnum.UPDATE_QUOTE_SERVICE_TEMPLATE,
            template
          );
        }
      });
  },

  deleteQuoteServiceTemplate(
    context: ItemContext,
    payload: number
  ): Promise<any> {
    return this.$services.quoteServiceTemplate
      .deleteQuoteTemplate(payload)
      .then(() => {
        context.commit(
          IQuoteTemplateMutationsEnum.REMOVE_QUOTE_SERVICE_TEMPLATE,
          payload
        );
      });
  },

  // SERVICE TRANSLATIONS
  fetchQuoteServiceTemplateTranslations(
    _context: ItemContext,
    payload: number
  ): Promise<IQuoteServiceTemplateData[]> {
    return this.$services.quoteServiceTemplate.fetchQuoteTemplateTranslations(
      payload
    );
  },

  fetchQuoteServiceTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationFetchAction
  ): Promise<IQuoteServiceTemplateData> {
    return this.$services.quoteServiceTemplate.fetchQuoteTemplateTranslation(
      payload.templateId,
      payload.translationId
    );
  },

  createQuoteServiceTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationUpdateAction<IQuoteServiceTemplateData>
  ): Promise<IQuoteServiceTemplateData> {
    return this.$services.quoteServiceTemplate.createQuoteTemplateTranslation(
      payload.templateId,
      payload.translation
    );
  },

  updateQuoteServiceTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationUpdateAction<IQuoteServiceTemplateData>
  ): Promise<IQuoteServiceTemplateData> {
    return this.$services.quoteServiceTemplate.updateQuoteTemplateTranslation(
      payload.templateId,
      payload.translation
    );
  },

  deleteQuoteServiceTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationFetchAction
  ): Promise<void> {
    return this.$services.quoteServiceTemplate.deleteQuoteTemplateTranslation(
      payload.templateId,
      payload.translationId
    );
  },

  // FREIGHT
  fetchQuoteFreightTemplates(context: ItemContext): Promise<void> {
    return this.$services.quoteFreightTemplate
      .fetchQuoteTemplates()
      .then(templates => {
        context.commit(
          IQuoteTemplateMutationsEnum.SET_QUOTE_FREIGHT_TEMPLATES,
          templates
        );
      });
  },

  fetchQuoteFreightTemplate(
    context: ItemContext,
    payload: number
  ): Promise<void> {
    return this.$services.quoteFreightTemplate
      .fetchQuoteTemplate(payload)
      .then(template => {
        const index = context.state.freightTemplates.findIndex(
          (v: IQuoteTemplateData) => v.id === template.id
        );
        if (index === -1) {
          context.commit(
            IQuoteTemplateMutationsEnum.ADD_QUOTE_FREIGHT_TEMPLATE,
            template
          );
        } else {
          context.commit(
            IQuoteTemplateMutationsEnum.UPDATE_QUOTE_FREIGHT_TEMPLATE,
            template
          );
        }
      });
  },

  createQuoteFreightTemplate(
    context: ItemContext,
    payload: IQuoteFreightTemplateData
  ): Promise<void> {
    return this.$services.quoteFreightTemplate
      .createQuoteTemplate(payload)
      .then(template => {
        context.commit(
          IQuoteTemplateMutationsEnum.ADD_QUOTE_FREIGHT_TEMPLATE,
          template
        );
      });
  },

  updateQuoteFreightTemplate(
    context: ItemContext,
    payload: IQuoteFreightTemplateData
  ): Promise<void> {
    return this.$services.quoteFreightTemplate
      .updateQuoteTemplate(payload)
      .then(template => {
        const index = context.state.freightTemplates.findIndex(
          v => v.id === template.id
        );
        if (index === -1) {
          context.commit(
            IQuoteTemplateMutationsEnum.ADD_QUOTE_FREIGHT_TEMPLATE,
            template
          );
        } else {
          context.commit(
            IQuoteTemplateMutationsEnum.UPDATE_QUOTE_FREIGHT_TEMPLATE,
            template
          );
        }
      });
  },

  deleteQuoteFreightTemplate(
    context: ItemContext,
    payload: number
  ): Promise<any> {
    return this.$services.quoteFreightTemplate
      .deleteQuoteTemplate(payload)
      .then(() => {
        context.commit(
          IQuoteTemplateMutationsEnum.REMOVE_QUOTE_FREIGHT_TEMPLATE,
          payload
        );
      });
  },

  // FREIGHTS TRANSLATIONS
  fetchQuoteFreightTemplateTranslations(
    _context: ItemContext,
    payload: number
  ): Promise<IQuoteTemplateData[]> {
    return this.$services.quoteFreightTemplate.fetchQuoteTemplateTranslations(
      payload
    );
  },

  fetchQuoteFreightTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationFetchAction
  ): Promise<IQuoteTemplateData> {
    return this.$services.quoteFreightTemplate.fetchQuoteTemplateTranslation(
      payload.templateId,
      payload.translationId
    );
  },

  createQuoteFreightTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationUpdateAction<IQuoteTemplateData>
  ): Promise<IQuoteTemplateData> {
    return this.$services.quoteFreightTemplate.createQuoteTemplateTranslation(
      payload.templateId,
      payload.translation
    );
  },

  updateQuoteFreightTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationUpdateAction<IQuoteTemplateData>
  ): Promise<IQuoteTemplateData> {
    return this.$services.quoteFreightTemplate.updateQuoteTemplateTranslation(
      payload.templateId,
      payload.translation
    );
  },

  deleteQuoteFreightTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationFetchAction
  ): Promise<void> {
    return this.$services.quoteFreightTemplate.deleteQuoteTemplateTranslation(
      payload.templateId,
      payload.translationId
    );
  },

  // Payment Terms
  fetchQuotePaymentTermTemplates(context: ItemContext): Promise<void> {
    return this.$services.quotePaymentTermTemplate
      .fetchQuoteTemplates()
      .then(templates => {
        context.commit(
          IQuoteTemplateMutationsEnum.SET_QUOTE_PAYMENT_TERM_TEMPLATES,
          templates
        );
      });
  },

  fetchQuotePaymentTermTemplate(
    context: ItemContext,
    payload: number
  ): Promise<void> {
    return this.$services.quotePaymentTermTemplate
      .fetchQuoteTemplate(payload)
      .then(template => {
        const index = context.state.paymentTermTemplates.findIndex(
          (v: IQuoteTemplateData) => v.id === template.id
        );
        if (index === -1) {
          context.commit(
            IQuoteTemplateMutationsEnum.ADD_QUOTE_PAYMENT_TERM_TEMPLATE,
            template
          );
        } else {
          context.commit(
            IQuoteTemplateMutationsEnum.UPDATE_QUOTE_PAYMENT_TERM_TEMPLATE,
            template
          );
        }
      });
  },

  createQuotePaymentTermTemplate(
    context: ItemContext,
    payload: IQuotePaymentTermTemplateData
  ): Promise<void> {
    return this.$services.quotePaymentTermTemplate
      .createQuoteTemplate(payload)
      .then(template => {
        context.commit(
          IQuoteTemplateMutationsEnum.ADD_QUOTE_PAYMENT_TERM_TEMPLATE,
          template
        );
      });
  },

  updateQuotePaymentTermTemplate(
    context: ItemContext,
    payload: IQuotePaymentTermTemplateData
  ): Promise<void> {
    return this.$services.quotePaymentTermTemplate
      .updateQuoteTemplate(payload)
      .then(template => {
        const index = context.state.paymentTermTemplates.findIndex(
          v => v.id === template.id
        );
        if (index === -1) {
          context.commit(
            IQuoteTemplateMutationsEnum.ADD_QUOTE_PAYMENT_TERM_TEMPLATE,
            template
          );
        } else {
          context.commit(
            IQuoteTemplateMutationsEnum.UPDATE_QUOTE_PAYMENT_TERM_TEMPLATE,
            template
          );
        }
      });
  },

  deleteQuotePaymentTermTemplate(
    context: ItemContext,
    payload: number
  ): Promise<any> {
    return this.$services.quotePaymentTermTemplate
      .deleteQuoteTemplate(payload)
      .then(() => {
        context.commit(
          IQuoteTemplateMutationsEnum.REMOVE_QUOTE_PAYMENT_TERM_TEMPLATE,
          payload
        );
      });
  },

  // PAYMENT TERMS TRANSLATIONS
  fetchQuotePaymentTermTemplateTranslations(
    _context: ItemContext,
    payload: number
  ): Promise<IQuoteTemplateData[]> {
    return this.$services.quotePaymentTermTemplate.fetchQuoteTemplateTranslations(
      payload
    );
  },

  fetchQuotePaymentTermTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationFetchAction
  ): Promise<IQuoteTemplateData> {
    return this.$services.quotePaymentTermTemplate.fetchQuoteTemplateTranslation(
      payload.templateId,
      payload.translationId
    );
  },

  createQuotePaymentTermTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationUpdateAction<IQuoteTemplateData>
  ): Promise<IQuoteTemplateData> {
    return this.$services.quotePaymentTermTemplate.createQuoteTemplateTranslation(
      payload.templateId,
      payload.translation
    );
  },

  updateQuotePaymentTermTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationUpdateAction<IQuoteTemplateData>
  ): Promise<IQuoteTemplateData> {
    return this.$services.quotePaymentTermTemplate.updateQuoteTemplateTranslation(
      payload.templateId,
      payload.translation
    );
  },

  deleteQuotePaymentTermTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationFetchAction
  ): Promise<void> {
    return this.$services.quotePaymentTermTemplate.deleteQuoteTemplateTranslation(
      payload.templateId,
      payload.translationId
    );
  },

  // Term
  fetchQuoteTermTemplates(
    context: ItemContext,
    payload?: IQuoteTemplateFilterParams
  ): Promise<void> {
    return this.$services.quoteTermTemplate
      .fetchQuoteTemplates(payload)
      .then(templates => {
        context.commit(
          IQuoteTemplateMutationsEnum.SET_QUOTE_TERM_TEMPLATES,
          templates
        );
      });
  },

  fetchQuoteTermTemplate(context: ItemContext, payload: number): Promise<void> {
    return this.$services.quoteTermTemplate
      .fetchQuoteTemplate(payload)
      .then(template => {
        const index = context.state.termTemplates.findIndex(
          (v: IQuoteTemplateData) => v.id === template.id
        );
        if (index === -1) {
          context.commit(
            IQuoteTemplateMutationsEnum.ADD_QUOTE_TERM_TEMPLATE,
            template
          );
        } else {
          context.commit(
            IQuoteTemplateMutationsEnum.UPDATE_QUOTE_TERM_TEMPLATE,
            template
          );
        }
      });
  },

  createQuoteTermTemplate(
    context: ItemContext,
    payload: IQuoteTermTemplateData
  ): Promise<void> {
    return this.$services.quoteTermTemplate
      .createQuoteTemplate(payload)
      .then(template => {
        context.commit(
          IQuoteTemplateMutationsEnum.ADD_QUOTE_TERM_TEMPLATE,
          template
        );
      });
  },

  updateQuoteTermTemplate(
    context: ItemContext,
    payload: IQuoteTermTemplateData
  ): Promise<void> {
    return this.$services.quoteTermTemplate
      .updateQuoteTemplate(payload)
      .then(template => {
        const index = context.state.termTemplates.findIndex(
          v => v.id === template.id
        );
        if (index === -1) {
          context.commit(
            IQuoteTemplateMutationsEnum.ADD_QUOTE_TERM_TEMPLATE,
            template
          );
        } else {
          context.commit(
            IQuoteTemplateMutationsEnum.UPDATE_QUOTE_TERM_TEMPLATE,
            template
          );
        }
      });
  },

  setQuoteTermTemplatesOrder(
    context: ItemContext,
    payload: IQuoteTemplateSetOrderPayload[]
  ): Promise<void> {
    return Promise.all(
      payload.map(
        async term =>
          await this.$services.quoteTermTemplate.setQuoteTemplateOrder(
            term.id,
            term.order
          )
      )
    ).then(terms => {
      context.commit(
        IQuoteTemplateMutationsEnum.SET_QUOTE_TERM_TEMPLATES_ORDER,
        terms
      );
    });
  },

  deleteQuoteTermTemplate(context: ItemContext, payload: number): Promise<any> {
    return this.$services.quoteTermTemplate
      .deleteQuoteTemplate(payload)
      .then(() => {
        context.commit(
          IQuoteTemplateMutationsEnum.REMOVE_QUOTE_TERM_TEMPLATE,
          payload
        );
      });
  },

  // TERMS TRANSLATIONS
  fetchQuoteTermTemplateTranslations(
    _context: ItemContext,
    payload: number
  ): Promise<IQuoteTermTemplateData[]> {
    return this.$services.quoteTermTemplate.fetchQuoteTemplateTranslations(
      payload
    );
  },

  fetchQuoteTermTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationFetchAction
  ): Promise<IQuoteTermTemplateData> {
    return this.$services.quoteTermTemplate.fetchQuoteTemplateTranslation(
      payload.templateId,
      payload.translationId
    );
  },

  createQuoteTermTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationUpdateAction<IQuoteTermTemplateData>
  ): Promise<IQuoteTermTemplateData> {
    return this.$services.quoteTermTemplate.createQuoteTemplateTranslation(
      payload.templateId,
      payload.translation
    );
  },

  updateQuoteTermTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationUpdateAction<IQuoteTermTemplateData>
  ): Promise<IQuoteTermTemplateData> {
    return this.$services.quoteTermTemplate.updateQuoteTemplateTranslation(
      payload.templateId,
      payload.translation
    );
  },

  deleteQuoteTermTemplateTranslation(
    _context: ItemContext,
    payload: IQuoteTemplateTranslationFetchAction
  ): Promise<void> {
    return this.$services.quoteTermTemplate.deleteQuoteTemplateTranslation(
      payload.templateId,
      payload.translationId
    );
  }
};
